import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import gameService from '../../../services/gameService';
import { AuthContext } from '../../../context/AuthContext';
import { Button, Spinner, Card, InputGroup, FormControl, ProgressBar } from 'react-bootstrap';
import {
  FaCoins, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaHistory,
  FaArrowLeft, FaCrown, FaPlay, FaRedoAlt, FaTimes
} from 'react-icons/fa';
import styles from './Bullfighter.module.css'; // 🔹 módulo aislado con :global

const CHIP_AMOUNTS = [1, 5, 10, 25, 50, 100];

const BullfighterOrBull = () => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const userId = auth.user?.id;
  const gameId = 2;

  const [betAmount, setBetAmount] = useState(1);
  const [chosenSide, setChosenSide] = useState('bullfighter');
  const [result, setResult] = useState(null);
  const [randomOutcome, setRandomOutcome] = useState(null);
  const [newBalance, setNewBalance] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchHistory = async () => {
      if (userId) {
        try {
          const data = await gameService.getPlayerHistory(userId, gameId);
          setHistory(data);
        } catch {
          setError(t('bullfighter.historyError'));
        }
      }
    };
    fetchHistory();
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, [userId, gameId, t]);

  const clampBet = (val) => Math.max(1, Math.floor(Number.isFinite(val) ? val : parseInt(val, 10) || 1));

  const handleChipClick = (amount, e) => {
    if (e && e.shiftKey) {
      setBetAmount((prev) => clampBet(prev + amount));
    } else {
      setBetAmount(clampBet(amount));
    }
  };

  const handlePlay = async () => {
    if (betAmount < 1) {
      setError(t('bullfighter.minBet'));
      return;
    }
    setLoading(true);
    setResult(null);
    setError(null);
    setRandomOutcome(null);
    setNewBalance(null);
    setIsSpinning(true);
    try {
      const response = await gameService.playBullfighterOrBull(betAmount, chosenSide);
      setTimeout(() => {
        setIsSpinning(false);
        setResult(response.result);
        setRandomOutcome(response.randomOutcome);
        setNewBalance(response.newBalance);
        setLoading(false);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setLoading(false);
      setIsSpinning(false);
    }
  };

  const getCoinClasses = (frontSide) => {
    const base = 'bullfighter-coin';
    if (isSpinning) return `${base} bullfighter-flip`;
    if (!randomOutcome) return base;
    const isHeads = randomOutcome === frontSide;
    return `${base} ${isHeads ? 'is-heads' : 'is-tails'}`;
  };

  return (
    <div className="casino-theme d-flex justify-content-center align-items-center">
      {isLoading ? (
        <div className="bullfighter-loading-container">
          <h2 className="bullfighter-loading-text">{t('bullfighter.loading')}</h2>
          <ProgressBar animated now={100} className="bullfighter-loading-bar" />
        </div>
      ) : (
        <Card className="text-center bullfighter-card shadow-lg">
          <Card.Body>
            <div className="position-relative mb-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/sessions')}
                className="bullfighter-back-button"
                aria-label={t('bullfighter.back')}
              >
                <FaArrowLeft className="me-2" /> {t('bullfighter.back')}
              </Button>

              <h1 className="bullfighter-casino-title text-center">
                <FaCrown className="text-warning me-2" /> {t('bullfighter.title')} <FaCrown className="text-warning ms-2" />
              </h1>
            </div>

            <div className="d-flex justify-content-around mb-3">
              <Button className="bullfighter-button" onClick={() => setShowRulesModal(true)}>
                <FaInfoCircle className="me-2" /> {t('bullfighter.rules')}
              </Button>
              <Button
                className="bullfighter-button"
                onClick={() => {
                  setShowHistoryModal(true);
                  if (userId) gameService.getPlayerHistory(userId, gameId).then(setHistory);
                }}
              >
                <FaHistory className="me-2" /> {t('bullfighter.history')}
              </Button>
            </div>

            <div className="d-flex justify-content-center flex-wrap gap-2 mb-2">
              {CHIP_AMOUNTS.map((amt) => {
                const selected = betAmount === amt && !isSpinning && !loading;
                const style = selected
                  ? { borderColor: 'rgba(255,215,0,.55)', boxShadow: '0 0 0 2px rgba(255,215,0,.35) inset, 0 0 18px rgba(255,215,0,.18)' }
                  : undefined;
                return (
                  <Button
                    key={amt}
                    type="button"
                    className="bullfighter-button px-3 py-2"
                    onClick={(e) => handleChipClick(amt, e)}
                    disabled={loading || isSpinning}
                    style={style}
                    aria-pressed={selected}
                  >
                    <FaCoins className="me-1" /> {amt}
                  </Button>
                );
              })}
            </div>

            <div className="mb-3">
              <InputGroup>
                <InputGroup.Text><FaCoins /></InputGroup.Text>
                <FormControl
                  type="number"
                  value={betAmount}
                  onChange={(e) => setBetAmount(clampBet(Number(e.target.value)))}
                  min="1"
                  step="1"
                  required
                  inputMode="numeric"
                  aria-label={t('bullfighter.bet')}
                />
                <InputGroup.Text>{t('bullfighter.bet')}</InputGroup.Text>
              </InputGroup>
            </div>

            <div className="d-flex justify-content-around mb-4 bullfighter-options">
              <div
                role="button"
                tabIndex={0}
                className={`bullfighter-option ${chosenSide === 'bull' ? 'selected' : ''}`}
                onClick={() => setChosenSide('bull')}
              >
                <div className={getCoinClasses('bull')}>
                  <div className="bullfighter-coin-inner">
                    <figure className="bullfighter-coin-face bullfighter-coin-front">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/games/BullfighterOrBull/Bull.webp`}
                        alt={t('bullfighter.bull')}
                        className="bullfighter-coin-img"
                      />
                    </figure>
                    <figure className="bullfighter-coin-face bullfighter-coin-back">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/games/BullfighterOrBull/Bullfighter.webp`}
                        alt={t('bullfighter.bullfighter')}
                        className="bullfighter-coin-img"
                      />
                    </figure>
                  </div>
                </div>
                <p className="mt-2 mb-0">{t('bullfighter.bull')}</p>
              </div>

              <div
                role="button"
                tabIndex={0}
                className={`bullfighter-option ${chosenSide === 'bullfighter' ? 'selected' : ''}`}
                onClick={() => setChosenSide('bullfighter')}
              >
                <div className={getCoinClasses('bullfighter')}>
                  <div className="bullfighter-coin-inner">
                    <figure className="bullfighter-coin-face bullfighter-coin-front">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/games/BullfighterOrBull/Bullfighter.webp`}
                        alt={t('bullfighter.bullfighter')}
                        className="bullfighter-coin-img"
                      />
                    </figure>
                    <figure className="bullfighter-coin-face bullfighter-coin-back">
                      <img
                        src={`${process.env.PUBLIC_URL}/images/games/BullfighterOrBull/Bull.webp`}
                        alt={t('bullfighter.bull')}
                        className="bullfighter-coin-img"
                      />
                    </figure>
                  </div>
                </div>
                <p className="mt-2 mb-0">{t('bullfighter.bullfighter')}</p>
              </div>
            </div>

            <div className="d-flex justify-content-center mb-3 gap-2">
              <Button className="bullfighter-button" onClick={handlePlay} disabled={loading || isSpinning}>
                {loading || isSpinning ? <Spinner animation="border" size="sm" /> : <><FaPlay className="me-2" /> {t('bullfighter.play')}</>}
              </Button>
              <Button
                className="bullfighter-button"
                onClick={() => {
                  setResult(null);
                  setRandomOutcome(null);
                  setNewBalance(null);
                  setIsSpinning(false);
                }}
                disabled={!result && !isSpinning}
              >
                <FaRedoAlt className="me-2" /> {t('bullfighter.restart')}
              </Button>
            </div>

            {result && (
              <div className="bullfighter-result mt-4">
                {result === 'win' ? (
                  <FaCheckCircle className="bullfighter-result-icon bullfighter-win-icon animate__animated animate__bounceIn" />
                ) : (
                  <FaTimesCircle className="bullfighter-result-icon bullfighter-lose-icon animate__animated animate__shakeX" />
                )}
                <h3 className="animate__animated animate__fadeIn">
                  {result === 'win' ? t('bullfighter.youWin') : t('bullfighter.youLose')}
                </h3>
                <p className="animate__animated animate__fadeIn">
                  {t('bullfighter.newBalance', { balance: newBalance })}
                </p>
                <div className="bullfighter-winner-image-container">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/games/BullfighterOrBull/${randomOutcome === 'bullfighter' ? 'Bullfighter' : 'Bull'}.webp`}
                    alt={randomOutcome || ''}
                    className="bullfighter-winner-image bullfighter-blinking animate__animated animate__zoomIn"
                  />
                </div>
              </div>
            )}

            {error && <p className="text-danger mt-3">{error}</p>}
          </Card.Body>
        </Card>
      )}

      {showRulesModal && (
        <div className="bullfighter-custom-modal" role="dialog" aria-modal="true">
          <div className="bullfighter-modal-content">
            <div className="bullfighter-modal-header">
              <h2><FaInfoCircle className="me-2" /> {t('bullfighter.rulesTitle')}</h2>
              <button className="bullfighter-modal-close" onClick={() => setShowRulesModal(false)} aria-label={t('bullfighter.close')}>
                <FaTimes />
              </button>
            </div>
            <div className="bullfighter-modal-body">
              <p>{t('bullfighter.rulesDesc')}</p>
              <ul className="list-unstyled">
                <li><FaCheckCircle className="me-2 text-success" />{t('bullfighter.rulesWin')}</li>
                <li><FaTimesCircle className="me-2 text-danger" />{t('bullfighter.rulesLose')}</li>
                <li><FaCoins className="me-2 text-warning" />{t('bullfighter.rulesMinBet')}</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div className="bullfighter-custom-modal" role="dialog" aria-modal="true">
          <div className="bullfighter-modal-content">
            <div className="bullfighter-modal-header">
              <h2><FaHistory className="me-2" /> {t('bullfighter.historyTitle')}</h2>
              <button className="bullfighter-modal-close" onClick={() => setShowHistoryModal(false)} aria-label={t('bullfighter.close')}>
                <FaTimes />
              </button>
            </div>
            <div className="bullfighter-modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {history.length > 0 ? (
                <ul className="list-group">
                  {history.slice(0, 100).map((entry, index) => (
                    <li key={index} className="list-group-item">
                      <strong>{entry.result === 'win' ? t('bullfighter.historyWin') : t('bullfighter.historyLose')}:</strong> {entry.score} MENEL<br />
                      <strong>{t('bullfighter.choice')}:</strong> {entry.details}<br />
                      <strong>{t('bullfighter.date')}:</strong> {new Date(entry.created_at).toLocaleString()}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t('bullfighter.historyEmpty')}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BullfighterOrBull;
